import { useState } from 'react';
import { Controller, type Control, type FieldValues, type Path, type RegisterOptions } from 'react-hook-form';
import { InputAdornment, IconButton, Stack, TextField, type TextFieldProps } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { PasswordRequirements } from './PasswordRequirements';

/**
 * Reusable password TextField with visibility toggle controlled by react-hook-form's Controller.
 *
 * Example:
 * <FormPasswordField control={control} name="password" label="Password" />
 * <FormPasswordField control={control} name="password" label="Password" showRequirements />
 */
type FormPasswordFieldProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  /**
   * Show password requirements and strength indicator below the field.
   * Useful for password creation fields.
   */
  showRequirements?: boolean;
} & Omit<TextFieldProps, 'name' | 'defaultValue' | 'value' | 'onChange' | 'inputRef' | 'type'>;

export function FormPasswordField<TFieldValues extends FieldValues>(props: FormPasswordFieldProps<TFieldValues>) {
  const {
    name,
    control,
    rules,
    helperText,
    error,
    variant = 'outlined',
    showRequirements = false,
    ...textFieldProps
  } = props;
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <Stack spacing={showRequirements ? 1.5 : 0}>
          <TextField
            helperText={helperText ?? fieldState.error?.message}
            {...textFieldProps}
            {...field}
            type={showPassword ? 'text' : 'password'}
            variant={variant}
            error={error ?? Boolean(fieldState.error)}
            fullWidth={textFieldProps.fullWidth ?? true}
            InputProps={{
              ...textFieldProps.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle visibility"
                    disabled={textFieldProps.disabled}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {showRequirements && <PasswordRequirements password={field.value || ''} />}
        </Stack>
      )}
    />
  );
}
